/**
 * Currency Selector Component
 * Allows users to switch between HBAR, USD, and INR display
 */

import React, { useState, useEffect } from 'react';
import { analyticsService, ExchangeRates } from '../services/analytics.service';

interface CurrencySelectorProps {
  selectedCurrency: 'HBAR' | 'USD' | 'INR';
  onCurrencyChange: (currency: 'HBAR' | 'USD' | 'INR') => void;
  showExchangeRates?: boolean;
}

export const CurrencySelector: React.FC<CurrencySelectorProps> = ({
  selectedCurrency,
  onCurrencyChange,
  showExchangeRates = true
}) => {
  const [exchangeRates, setExchangeRates] = useState<ExchangeRates | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (showExchangeRates) {
      fetchExchangeRates();
    }
  }, [showExchangeRates]);

  const fetchExchangeRates = async () => {
    try {
      setLoading(true);
      const rates = await analyticsService.getCurrentExchangeRates();
      setExchangeRates(rates);
    } catch (error) {
      console.error('Error fetching exchange rates:', error);
    } finally {
      setLoading(false);
    }
  };

  const currencies = [
    { value: 'HBAR', label: 'HBAR', symbol: 'ℏ' },
    { value: 'USD', label: 'USD', symbol: '$' },
    { value: 'INR', label: 'INR', symbol: '₹' }
  ] as const;

  const formatExchangeRate = (rate: number, fromCurrency: string, toCurrency: string) => {
    if (rate < 0.01) {
      return `1 ${fromCurrency} = ${rate.toFixed(8)} ${toCurrency}`;
    } else if (rate < 1) {
      return `1 ${fromCurrency} = ${rate.toFixed(4)} ${toCurrency}`;
    } else {
      return `1 ${fromCurrency} = ${rate.toFixed(2)} ${toCurrency}`;
    }
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">Currency Display</h3>
        {showExchangeRates && (
          <button
            onClick={fetchExchangeRates}
            disabled={loading}
            className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
          >
            {loading ? 'Updating...' : 'Refresh Rates'}
          </button>
        )}
      </div>

      {/* Currency Selection */}
      <div className="flex space-x-2 mb-4">
        {currencies.map((currency) => (
          <button
            key={currency.value}
            onClick={() => onCurrencyChange(currency.value)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              selectedCurrency === currency.value
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {currency.symbol} {currency.label}
          </button>
        ))}
      </div>

      {/* Exchange Rates Display */}
      {showExchangeRates && exchangeRates && (
        <div className="border-t pt-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Current Exchange Rates</h4>
          <div className="space-y-1 text-sm text-gray-600">
            <div>{formatExchangeRate(exchangeRates.hbarToUsd, 'HBAR', 'USD')}</div>
            <div>{formatExchangeRate(exchangeRates.hbarToInr, 'HBAR', 'INR')}</div>
            <div>{formatExchangeRate(exchangeRates.usdToInr, 'USD', 'INR')}</div>
          </div>
          <div className="mt-2 text-xs text-gray-500">
            Last updated: {new Date(exchangeRates.timestamp).toLocaleString()}
            <br />
            Source: {exchangeRates.source}
          </div>
        </div>
      )}
    </div>
  );
};

export default CurrencySelector;